'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import Map, { MapRef, Marker, Popup } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { IconMapPin } from '@tabler/icons-react';
import { cn } from 'ui';

const MAPBOX_ACCESS_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN || '';

const SINGLE_POINT_ZOOM = 14;
const FIT_BOUNDS_OPTIONS = { padding: 56, maxZoom: 15 };

export interface LocationMapPoint {
  id: string;
  latitude: number;
  longitude: number;
  color?: string;
  label?: string;
  subtitle?: string;
  details?: string[];
}

interface LocationMapProps {
  points: LocationMapPoint[];
  className?: string;
  height?: string;
  emptyHint?: string;
  // Opens that point's popup and flies to it. Lets a list beside the map drive
  // the camera without the caller reaching for the map instance.
  focusedId?: string | null;
}

const hasPopupContent = (point: LocationMapPoint) =>
  Boolean(point.label || point.subtitle || point.details?.length);

// Read-only display map: renders one or more pins and fits the viewport to them.
// A single point centers with a fixed zoom; multiple points fit their bounding
// box. Used for the branch detail (one pin) and the all-branches overview.
export const LocationMap = ({
  points,
  className,
  height = '20rem',
  emptyHint,
  focusedId,
}: LocationMapProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const mapRef = useRef<MapRef>(null);

  const bounds = useMemo(() => {
    if (points.length < 2) return null;

    const lats = points.map((point) => point.latitude);
    const lngs = points.map((point) => point.longitude);

    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ] as [[number, number], [number, number]];
  }, [points]);

  const initialViewState = useMemo(() => {
    if (!points.length) return undefined;
    if (bounds) return { bounds, fitBoundsOptions: FIT_BOUNDS_OPTIONS };

    const [point] = points;
    return {
      latitude: point.latitude,
      longitude: point.longitude,
      zoom: SINGLE_POINT_ZOOM,
    };
  }, [bounds, points]);

  // What the camera should frame, by value: callers build `points` inline, so
  // the identity of the array changes on every render and cannot drive this.
  const cameraKey = points
    .map((point) => `${point.latitude},${point.longitude}`)
    .join('|');

  // Seeded with what mounting already framed, so only a later change animates.
  const framedRef = useRef(cameraKey);

  // `initialViewState` is read once, at mount. Without this, picking another
  // point moves the pin and leaves the camera behind.
  useEffect(() => {
    const map = mapRef.current;

    if (!map || !points.length || framedRef.current === cameraKey) return;

    framedRef.current = cameraKey;

    if (bounds) {
      map.fitBounds(bounds, FIT_BOUNDS_OPTIONS);
      return;
    }

    const [point] = points;

    map.flyTo({
      center: [point.longitude, point.latitude],
      zoom: SINGLE_POINT_ZOOM,
    });
  }, [bounds, cameraKey, points]);

  // Focusing does not change `points`, so it never races the camera effect above.
  useEffect(() => {
    const map = mapRef.current;
    const focused = points.find((point) => point.id === focusedId);

    if (!map || !focused) return;

    setActiveId(focused.id);
    map.flyTo({
      center: [focused.longitude, focused.latitude],
      zoom: SINGLE_POINT_ZOOM,
    });
  }, [focusedId, points]);

  if (!points.length || !initialViewState) {
    return (
      <div
        className={cn(
          'rounded-md border bg-muted flex flex-col items-center justify-center text-center gap-2 p-4',
          className,
        )}
        style={{ height }}
      >
        <IconMapPin className='h-8 w-8 text-muted-foreground' />
        {emptyHint && (
          <p className='text-sm text-muted-foreground'>{emptyHint}</p>
        )}
      </div>
    );
  }

  const activePoint = points.find((point) => point.id === activeId);

  return (
    <div
      className={cn('relative rounded-md overflow-hidden border', className)}
      style={{ height }}
    >
      <Map
        ref={mapRef}
        initialViewState={initialViewState}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={{ width: '100%', height: '100%' }}
        mapStyle='mapbox://styles/mapbox/standard'
        attributionControl={false}
      >
        {points.map((point) => (
          <Marker
            key={point.id}
            longitude={point.longitude}
            latitude={point.latitude}
            color={point.color ?? '#FC762D'}
            onClick={(event) => {
              event.originalEvent.stopPropagation();
              if (hasPopupContent(point)) setActiveId(point.id);
            }}
          />
        ))}

        {activePoint && hasPopupContent(activePoint) && (
          <Popup
            longitude={activePoint.longitude}
            latitude={activePoint.latitude}
            onClose={() => setActiveId(null)}
            closeButton={false}
            anchor='bottom'
            offset={28}
          >
            <div className='space-y-0.5'>
              {activePoint.label && (
                <p className='text-xs font-medium'>{activePoint.label}</p>
              )}
              {activePoint.subtitle && (
                <p className='text-[11px] text-muted-foreground'>
                  {activePoint.subtitle}
                </p>
              )}
              {activePoint.details?.map((detail, index) => (
                <p key={index} className='text-[11px] text-muted-foreground'>
                  {detail}
                </p>
              ))}
            </div>
          </Popup>
        )}
      </Map>
    </div>
  );
};
